import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useColors } from '../../theme/ColorModeContext';
import { getTechQuizByTrack } from './registry';
import { QUESTIONS_PER_DIFFICULTY, DIFFICULTY_ORDER } from './quizEngine';

const TOTAL = QUESTIONS_PER_DIFFICULTY * DIFFICULTY_ORDER.length;

interface QuizCalloutProps {
    /** Academy track slug (see skillTreeData `academyTracks`). */
    trackSlug: string | undefined;
    mb?: number | string;
}

/**
 * Slim "check your knowledge" strip shown above a skill tree. Renders nothing for
 * tracks that have no quiz. Practice only: the quiz awards no points.
 */
const QuizCallout = ({ trackSlug, mb = '32px' }: QuizCalloutProps) => {
    const colors = useColors();
    const quiz = getTechQuizByTrack(trackSlug);
    if (!quiz) return null;

    return (
        <Box
            sx={{
                mb,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '14px',
                p: { xs: '14px 16px', md: '14px 20px' },
                borderRadius: '8px',
                border: `1px solid ${colors.border.subtle}`,
                borderLeft: `2px solid ${colors.accent.cyan}`,
                backgroundColor: colors.bg.tertiary,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                <Box
                    sx={{
                        width: 34,
                        height: 34,
                        flexShrink: 0,
                        borderRadius: '7px',
                        display: 'grid',
                        placeItems: 'center',
                        backgroundColor: `${colors.accent.cyan}1a`,
                        border: `1px solid ${colors.accent.cyan}55`,
                    }}
                >
                    <QuizOutlinedIcon sx={{ fontSize: '1.1rem', color: colors.accent.cyan }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: '"Share Tech Mono", monospace',
                            fontSize: '0.72rem',
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: colors.accent.cyan,
                            lineHeight: 1.4,
                        }}
                    >
                        {'// Check your knowledge'}
                    </Typography>
                    <Typography sx={{ color: colors.text.secondary, fontSize: '0.86rem', lineHeight: 1.5 }}>
                        {TOTAL} random questions on {quiz.topic}: 20 minutes, no points, just a gut check.
                    </Typography>
                </Box>
            </Box>

            <Button
                component={RouterLink}
                to={quiz.path}
                variant="contained"
                size="small"
                endIcon={<ArrowForwardIcon />}
                sx={{ flexShrink: 0, fontFamily: '"Orbitron", sans-serif', fontWeight: 700 }}
            >
                Start quiz
            </Button>
        </Box>
    );
};

export default QuizCallout;
